import React, { useEffect, useMemo, useRef, useState, startTransition, useDeferredValue } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  HStack,
  VStack,
  Heading,
  Divider,
  Image,
  useToast,
  Text,
  Switch,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';
import AdminLayout from '@/layouts/AdminLayout';
import ScoreboardPreview from '@/components/scoreboard/ScoreboardPreview';
import ColorPicker from '@/components/common/ColorPicker';
import { HelpTooltipCard } from '@/components/common/HelpTooltipCard';
import UploadPanel, { UploadPanelFile } from '@/components/common/UploadPanel';
import {
  getScoreboardState,
  saveScoreboardState,
  updateAdminScoreboard,
  ScoreboardState,
  deriveShort,
  derivePrimaryFromLogo,
  ScoreboardTheme,
  resetTimer,
  listPresets,
  savePreset,
  loadPreset,
  listSponsorsAdmin,
  uploadSponsors,
  deleteSponsor,
  prefillSponsorsFromPage,
  getQr,
  uploadQr,
  deleteQr,
} from '@/services/scoreboard';
import { useFacrApi } from '@/hooks/useFacrApi';
import { SearchResult } from '@/services/facr/types';
import { API_URL } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import { AdminMatch, fetchAdminMatches, fetchTeamLogoOverrides } from '@/services/adminMatches';
import { getFacrClubInfoCache } from '@/services/facr/cache';
import { createSponsor } from '@/services/sponsors';

const themes: ScoreboardTheme[] = ['classic', 'pill', 'var1', 'var2', 'var3', 'var4'];

const resolveLogoUrl = (u?: string | null) => {
  if (!u) return undefined;
  if (u.startsWith('/uploads') || u.startsWith('/dist') || u.startsWith('/api/')) return u;
  if (/^https?:\/\//i.test(u)) {
    const base = (API_URL || '').replace(/\/$/, '');
    return `${base}/proxy/image?url=${encodeURIComponent(u)}`;
  }
  return u;
};

const toAbsoluteUploadUrl = (u?: string | null) => {
  try {
    if (!u) return u || undefined;
    const base = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
    const origin = `${base.protocol}//${base.host}`;
    return u.startsWith('/uploads/') ? (origin + u) : u;
  } catch {
    return u || undefined;
  }
};

const deproxify = (u?: string | null) => {
  try {
    if (!u) return u || undefined;
    const base = new URL(API_URL || '', typeof window !== 'undefined' ? window.location.origin : undefined);
    const parsed = new URL(u, base.origin);
    if (/\/proxy\/image$/i.test(parsed.pathname)) {
      const inner = parsed.searchParams.get('url');
      return inner || u || undefined;
    }
    return u || undefined;
  } catch {
    return u || undefined;
  }
};

const ScoreboardAdminPage: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const [state, setState] = useState<ScoreboardState | null>(null);
  const [loading, setLoading] = useState(true);
  // Local draft colors for smooth dragging - use refs to avoid re-renders
  const primaryDraftRef = useRef<string>(state?.primaryColor || '#1e3a8a');
  const secondaryDraftRef = useRef<string>(state?.secondaryColor || '#2563eb');
  const homeTextDraftRef = useRef<string>(state?.homeTextColor || '#ffffff');
  const awayTextDraftRef = useRef<string>(state?.awayTextColor || '#ffffff');
  const [primaryDraft, setPrimaryDraft] = useState<string>(state?.primaryColor || '#1e3a8a');
  const [secondaryDraft, setSecondaryDraft] = useState<string>(state?.secondaryColor || '#2563eb');
  const [homeTextDraft, setHomeTextDraft] = useState<string>(state?.homeTextColor || '#ffffff');
  const [awayTextDraft, setAwayTextDraft] = useState<string>(state?.awayTextColor || '#ffffff');
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<number>(0); // 0 upcoming, 1 recent
  // Presets & sponsors state
  const [presets, setPresets] = useState<string[]>([]);
  const [presetName, setPresetName] = useState('');
  const [sponsors, setSponsors] = useState<string[]>([]);
  const [qrUrl, setQrUrl] = useState<string>('');
  const { isOpen: isSponsorModalOpen, onOpen: openSponsorModal, onClose: closeSponsorModal } = useDisclosure();
  const [uploadedSponsorUrls, setUploadedSponsorUrls] = useState<string[]>([]);
  const [uploadedNewSponsors, setUploadedNewSponsors] = useState<Array<{ url: string; urlAbs: string; name: string; website: string }>>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const { isOpen: isBulkDeleteOpen, onOpen: openBulkDelete, onClose: closeBulkDelete } = useDisclosure();
  const cancelBulkRef = useRef<HTMLButtonElement | null>(null);
  const [addingSponsors, setAddingSponsors] = useState(false);
  const [isPickingColor, setIsPickingColor] = useState(false);
  const deferredState = useDeferredValue(state);
  const previewState = useMemo(() => {
    // Use refs for instant preview during dragging, fallback to state
    const base = deferredState || state;
    if (!base) return null;
    return {
      ...base,
      primaryColor: primaryDraftRef.current || base.primaryColor || '#1e3a8a',
      secondaryColor: secondaryDraftRef.current || base.secondaryColor || '#2563eb',
      homeTextColor: homeTextDraftRef.current || base.homeTextColor || '#ffffff',
      awayTextColor: awayTextDraftRef.current || base.awayTextColor || '#ffffff',
    } as ScoreboardState;
  }, [deferredState, state]);
  const saveDebounceRef = useRef<number | undefined>(undefined);
  const pendingPatchRef = useRef<Partial<ScoreboardState>>({});
  const setPartialDebounced = (patch: Partial<ScoreboardState>) => {
    startTransition(() => {
      setState((prev) => ({ ...(prev as ScoreboardState), ...patch }));
    });
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    if (saveDebounceRef.current) {
      window.clearTimeout(saveDebounceRef.current);
    }
    saveDebounceRef.current = window.setTimeout(async () => {
      const toSave = pendingPatchRef.current;
      pendingPatchRef.current = {};
      saveDebounceRef.current = undefined;
      try {
        const next = await saveScoreboardState(toSave);
        setState(next);
      } catch {}
    }, 250);
  };

  // For performance-sensitive inputs (color pickers): queue save, but don't re-render immediately on every drag
  const queueSaveOnly = (patch: Partial<ScoreboardState>) => {
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    if (saveDebounceRef.current) {
      window.clearTimeout(saveDebounceRef.current);
    }
    saveDebounceRef.current = window.setTimeout(() => {
      const toSave = pendingPatchRef.current;
      pendingPatchRef.current = {};
      saveDebounceRef.current = undefined;
      // Update UI immediately (non-urgent) without waiting for network
      startTransition(() => {
        setState((prev) => ({ ...(prev as ScoreboardState), ...toSave }));
      });
      // Persist asynchronously; ignore result to avoid blocking UI
      try { void saveScoreboardState(toSave); } catch {}
    }, 250);
  };

  // Commit any draft color values (called on pointer up/blur)
  const commitDraftColors = () => {
    if (!state) return;
    const patch: Partial<ScoreboardState> = {
      primaryColor: primaryDraftRef.current || state.primaryColor || '#1e3a8a',
      secondaryColor: secondaryDraftRef.current || state.secondaryColor || '#2563eb',
      homeTextColor: homeTextDraftRef.current || state.homeTextColor || '#ffffff',
      awayTextColor: awayTextDraftRef.current || state.awayTextColor || '#ffffff',
    };
    queueSaveOnly(patch);
    // Sync state displays with refs after commit
    setPrimaryDraft(primaryDraftRef.current);
    setSecondaryDraft(secondaryDraftRef.current);
    setHomeTextDraft(homeTextDraftRef.current);
    setAwayTextDraft(awayTextDraftRef.current);
  };

  // Club search inline (home/away target)
  const [clubQuery, setClubQuery] = useState('');
  const [assignTo, setAssignTo] = useState<'home' | 'away'>('home');
  const { searchClubs, searchResults, searchLoading, searchError } = useFacrApi();

  useEffect(() => {
    (async () => {
      const s = await getScoreboardState();
      setState(s);
      setLoading(false);
      // load presets & sponsors lists
      try { setPresets(await listPresets()); } catch {}
      try { setSponsors(await listSponsorsAdmin()); } catch {}
      try { setQrUrl(await getQr()); } catch {}
    })();
  }, []);

  const uploadSponsorFile = async (file: File): Promise<UploadPanelFile> => {
    const res = await uploadSponsors([file]);
    const rels = (res?.files || []).filter(Boolean) as string[];
    if (rels.length > 0) {
      const items = rels.map((rel) => {
        const abs = toAbsoluteUploadUrl(rel) as string;
        const baseName = (rel.split('/').pop() || '').replace(/\.[a-z0-9]+$/i, '');
        return { url: rel, urlAbs: abs, name: baseName, website: '' };
      });
      setUploadedSponsorUrls((prev) => [
        ...prev,
        ...rels.map((u) => toAbsoluteUploadUrl(u) as string),
      ]);
      setUploadedNewSponsors((prev) => [...prev, ...items]);
      if (!isSponsorModalOpen) {
        openSponsorModal();
      }
    }
    try {
      setSponsors(await listSponsorsAdmin());
    } catch {}
    const rel = rels[0] || '';
    const abs = rel ? (toAbsoluteUploadUrl(rel) as string) : undefined;
    return {
      url: abs || rel || '',
      name: file.name,
      type: file.type || 'image/*',
      size: file.size,
    };
  };

  const uploadQrFile = async (file: File): Promise<UploadPanelFile> => {
    await uploadQr(file);
    const url = await getQr();
    setQrUrl(url);
    return {
      url: url || '',
      name: file.name,
      type: file.type || 'image/*',
      size: file.size,
    };
  };

  useEffect(() => {
    if (!state) return;
    if (isPickingColor) return;
    // Update both refs and state when colors change from external sources
    primaryDraftRef.current = state.primaryColor || '#1e3a8a';
    secondaryDraftRef.current = state.secondaryColor || '#2563eb';
    homeTextDraftRef.current = state.homeTextColor || '#ffffff';
    awayTextDraftRef.current = state.awayTextColor || '#ffffff';
    setPrimaryDraft(primaryDraftRef.current);
    setSecondaryDraft(secondaryDraftRef.current);
    setHomeTextDraft(homeTextDraftRef.current);
    setAwayTextDraft(awayTextDraftRef.current);
  }, [state?.primaryColor, state?.secondaryColor, state?.homeTextColor, state?.awayTextColor, isPickingColor]);

  useEffect(() => {
    return () => {
      if (saveDebounceRef.current) {
        window.clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = undefined;
      }
      pendingPatchRef.current = {};
    };
  }, []);

  // Polling is handled by React Query refetchInterval in the useQuery below

  // Load matches for linking
  const { data: adminMatches = [] } = useQuery<AdminMatch[]>({
    queryKey: ['admin-matches'],
    queryFn: fetchAdminMatches,
    staleTime: 60_000,
  });

  // Load team overrides (names + logos)
  const { data: teamOverrides = {} } = useQuery<any>({
    queryKey: ['team-logo-overrides-admin'],
    queryFn: fetchTeamLogoOverrides,
    staleTime: 5 * 60 * 1000,
  });
  const byId: Record<string, { name?: string; logo_url?: string }> = (teamOverrides as any)?.by_id || {};
  const byNameMap: Record<string, string> = (teamOverrides as any)?.by_name || {} as Record<string, string>;
  const normName = (s?: string) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-')
    .replace(/\bn\.?\b/g, ' nad ')
    .replace(/\bp\.?\b/g, ' pod ')
    .replace(/[\,\s]*(z\.?\s*s\.?|o\.?\s*s\.?)\s*$/g, '')
    .replace(/[\.,!;:()\[\]{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const aliasNameIndex = React.useMemo(() => {
    const urlToName: Record<string, string> = {};
    for (const v of Object.values(byId || {})) {
      const nm = String((v as any)?.name || '').trim();
      const lg = String((v as any)?.logo_url || '').trim();
      if (nm && lg) urlToName[lg] = nm;
    }
    const idx: Record<string, string> = {};
    for (const [alias, url] of Object.entries(byNameMap || {})) {
      const canon = urlToName[String(url)] || '';
      const key = normName(alias);
      if (canon && key) idx[key] = canon;
    }
    return idx;
  }, [byId, byNameMap]);
  const nameIndex = React.useMemo(() => {
    const idx: Record<string, { id: string; name: string; logo_url: string }> = {};
    try {
      for (const [id, v] of Object.entries(byId || {})) {
        const nm = String((v as any)?.name || '').trim();
        const lg = String((v as any)?.logo_url || '').trim();
        if (!nm) continue;
        const key = normName(nm);
        if (!key) continue;
        idx[key] = { id, name: nm, logo_url: lg } as any;
      }
    } catch {}
    return idx;
  }, [byId]);
  const getOverrideName = (teamName?: string, teamId?: string) => {
    const tid = teamId ? String(teamId) : '';
    if (tid && byId?.[tid]?.name && String(byId[tid].name).trim()) {
      return String(byId[tid].name).trim();
    }
    try {
      const n = normName(teamName);
      if (aliasNameIndex[n]) return aliasNameIndex[n];
      let hit: any = nameIndex[n];
      if (!hit) {
        for (const [k, v] of Object.entries(nameIndex)) {
          if (!k) continue;
          if (n.endsWith(k) || k.endsWith(n)) { hit = v; break; }
        }
      }
      if (hit && (hit as any).name) return String((hit as any).name);
    } catch {}
    return String(teamName || '');
  };
  const getLogo = (teamName?: string, original?: string) => {
    const byName = (teamOverrides as any)?.by_name || {} as Record<string, string>;
    const norm = (s: string) => String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const stripPrefixes = (s: string) => {
      let x = norm(s);
      x = x.replace(/\b(mestsky|m\.?f\.?k\.?|mfk|tj|sk|sokol|fotbalovy|fotbalový|fotbalovy\s+klub|fotbalovy\s+klub)\b/g, '');
      return x.replace(/\s+/g, ' ').trim();
    };
    const byNameNorm: Record<string, string> = Object.keys(byName || {}).reduce((acc: Record<string, string>, k) => { acc[norm(k)] = byName[k]; return acc; }, {});
    const strippedPairs = Object.keys(byName || {}).map((k) => ({ key: stripPrefixes(k), url: byName[k] }));
    const pick = (name?: string, orig?: string) => {
      if (!name) return orig;
      const exact = byName[name];
      let candidate = exact || byNameNorm[norm(name)];
      if (!candidate) {
        const s = stripPrefixes(name);
        for (const { key, url } of strippedPairs) { if (key && (s.endsWith(key) || key.endsWith(s))) { candidate = url; break; } }
      }
      return candidate || orig;
    };
    return pick(teamName, original);
  };

  // Load competitions/matches from cached FACR blob
  const { data: facrCache } = useQuery<any>({
    queryKey: ['facr-club-info-cache'],
    queryFn: getFacrClubInfoCache,
    staleTime: 600_000, // Increased from 120_000 to 600_000 (10 minutes)
    refetchInterval: 600_000, // Increased from 120_000 to 600_000 (10 minutes)
  });

  // Normalize matches from FACR cache to AdminMatch-ish shape
  const facrMatches: AdminMatch[] = useMemo(() => {
    const out: AdminMatch[] = [];
    try {
      const comps = facrCache?.competitions || [];
      for (const c of comps) {
        const cname = String(c?.name || c?.code || '').trim();
        const matches = c?.matches || [];
        for (const m of matches) {
          out.push({
            match_id: m?.match_id || m?.id || '',
            id: m?.match_id || m?.id || '',
            home: m?.home || '',
            away: m?.away || '',
            date_time: m?.date_time || m?.kickoff || '',
            home_logo_url: m?.home_logo_url || '',
            away_logo_url: m?.away_logo_url || '',
            competition: cname,
            league: cname,
          });
        }
      }
    } catch {}
    return out;
  }, [facrCache]);

  // Build competition list from both sources
  const competitions = React.useMemo(() => {
    const set = new Set<string>();
    for (const m of adminMatches) {
      const comp = String(m.competition || m.league || '').trim();
      if (comp) set.add(comp);
    }
    for (const m of facrMatches) {
      const comp = String((m as any).competition || (m as any).league || '').trim();
      if (comp) set.add(comp);
    }
    return ['Vše', ...Array.from(set)];
  }, [adminMatches, facrMatches]);

  const [compFilter, setCompFilter] = useState<string>('Vše');
  const [matchQuery, setMatchQuery] = useState('');

  const parsedMatches = React.useMemo(() => {
    const parseTs = (m: AdminMatch) => {
      const dt = String(m.date_time || m.date || '').slice(0, 16);
      // Accept RFC3339 or "YYYY-MM-DD HH:MM"
      let t = Date.parse(dt);
      if (isNaN(t) && dt.includes(' ')) {
        t = Date.parse(dt.replace(' ', 'T') + ':00');
      }
      return t;
    };
    const now = Date.now();
    let list: AdminMatch[] = [...(adminMatches || []), ...facrMatches];
    if (compFilter && compFilter !== 'Vše') {
      list = list.filter((m) => String((m as any).competition || (m as any).league || '').trim() === compFilter);
    }
    if (matchQuery.trim()) {
      const q = matchQuery.trim().toLowerCase();
      list = list.filter((m) =>
        String(m.home || '').toLowerCase().includes(q) ||
        String(m.away || '').toLowerCase().includes(q) ||
        String((m as any).competition || (m as any).league || '').toLowerCase().includes(q)
      );
    }
    const withTs = list.map((m) => ({ m, ts: parseTs(m) }));
    const upcoming = withTs
      .filter((x) => typeof x.ts === 'number' && !isNaN(x.ts) && x.ts >= now)
      .sort((a, b) => a.ts - b.ts)
      .map((x) => x.m);
    const recent = withTs
      .filter((x) => typeof x.ts === 'number' && !isNaN(x.ts) && x.ts < now)
      .sort((a, b) => b.ts - a.ts)
      .map((x) => x.m);
    return { upcoming, recent };
  }, [adminMatches, facrMatches, compFilter, matchQuery]);

  useEffect(() => {
    const q = clubQuery.trim();
    if (!q) return;
    const t = setTimeout(() => {
      searchClubs(q).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [clubQuery, searchClubs]);

  const setPartial = async (patch: Partial<ScoreboardState>) => {
    const next = await saveScoreboardState(patch);
    setState(next);
  };

  const applyMatch = async (m: AdminMatch) => {
    if (!state) return;
    // Populate names, logos and short codes
    const rawHomeName = String(m.home || (m as any).home_team || '').trim();
    const rawAwayName = String(m.away || (m as any).away_team || '').trim();
    const homeTeamId = String((m as any).home_id || (m as any).homeTeamId || (m as any).home_team_id || '');
    const awayTeamId = String((m as any).away_id || (m as any).awayTeamId || (m as any).away_team_id || '');
    const homeName = getOverrideName(rawHomeName, homeTeamId) || rawHomeName;
    const awayName = getOverrideName(rawAwayName, awayTeamId) || rawAwayName;
    // Prefer ID-based logo override, then name-based, then original logo URL
    const homeLogoRaw = (homeTeamId && byId?.[homeTeamId]?.logo_url)
      ? String(byId[homeTeamId].logo_url)
      : getLogo(rawHomeName, m.home_logo_url || (m as any).homeLogoURL || '');
    const awayLogoRaw = (awayTeamId && byId?.[awayTeamId]?.logo_url)
      ? String(byId[awayTeamId].logo_url)
      : getLogo(rawAwayName, m.away_logo_url || (m as any).awayLogoURL || '');
    const homeLogo = homeLogoRaw || '';
    const awayLogo = awayLogoRaw || '';
    const updates: Partial<ScoreboardState> = {
      homeName,
      awayName,
      homeShort: deriveShort(homeName),
      awayShort: deriveShort(awayName),
      homeLogo: homeLogo || state.homeLogo,
      awayLogo: awayLogo || state.awayLogo,
      externalMatchId: String(m.match_id || m.id || ''),
    };
    // Try to detect colors from logos
    const [cHome, cAway] = await Promise.all([
      derivePrimaryFromLogo(deproxify(homeLogo || state.homeLogo)),
      derivePrimaryFromLogo(deproxify(awayLogo || state.awayLogo)),
    ]);
    if (cHome) updates.primaryColor = cHome;
    if (cAway) updates.secondaryColor = cAway;
    const next = await saveScoreboardState(updates);
    setState(next);
    toast({ title: 'Zápas vybrán', description: 'Základní údaje byly předvyplněny.', status: 'success' });
  };

  const applyClub = async (club: SearchResult) => {
    const logoRaw = club.logo_url || undefined;
    const color = await derivePrimaryFromLogo(deproxify(logoRaw) || undefined);
    if (assignTo === 'home') {
      await setPartial({
        homeName: club.name || 'DOMÁCÍ',
        homeShort: deriveShort(club.name || ''),
        homeLogo: logoRaw,
        primaryColor: color || state?.primaryColor,
      });
    } else {
      await setPartial({
        awayName: club.name || 'HOSTÉ',
        awayShort: deriveShort(club.name || ''),
        awayLogo: logoRaw,
        secondaryColor: color || state?.secondaryColor,
      });
    }
    toast({ title: `Nastaveno pro ${assignTo === 'home' ? 'domácí' : 'hosty'}`, status: 'success' });
  };

  if (loading || !state || !previewState) return (
    <AdminLayout requireAdmin={false}>
      <Box>Načítání…</Box>
    </AdminLayout>
  );

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <Heading size="lg" mb={2}>Tabule (Scoreboard)</Heading>
        <Text color="gray.500" mb={2}>Napojte tabuli na konkrétní zápas a ovládejte skóre v reálném čase. Pokud je tabule propojená se zápasem, propisujeme živé skóre i do veřejné homepage.</Text>
        <HStack spacing={3} mb={4}>
          <Badge colorScheme="green">OBS</Badge>
          {(() => { const href = (typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '') + '/overlay/scoreboard'; return (
            <>
              <HelpTooltipCard
                label="Jak použít overlay"
                title="Použití scoreboard overlay v OBS"
                items={[
                  'V OBS přidejte nový Browser Source a vložte sem uvedenou URL overlaye.',
                  'Doporučená šířka zdroje je 1920 px, pozadí zůstává průhledné nad přenosem.',
                  'Overlay je veřejný – sdílejte URL jen s lidmi, kteří spravují přenos a výsledky.',
                ]}
              />
              <Button as="a" href={href} target="_blank" rel="noreferrer">Otevřít overlay</Button>
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.500">Veřejná URL pro OBS: {href}</Text>
                <Button size="sm" leftIcon={<CopyIcon />} onClick={() => { try { navigator.clipboard.writeText(href); } catch (_) {} }}>Kopírovat</Button>
              </HStack>
            </>
          ); })()}
        </HStack>

        {/* Match linking (initial element) */}
        <Box borderWidth="1px" borderRadius="lg" p={4} bg={cardBg} mb={6}>
          <Heading size="md" mb={3}>Napojení na zápas (FAČR)</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
            <FormControl>
              <FormLabel>Soutěž</FormLabel>
              <Select value={compFilter} onChange={(e) => setCompFilter(e.target.value)}>
                {competitions.map((c) => (<option key={c} value={c}>{c}</option>))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Hledat zápas</FormLabel>
              <Input placeholder="Hledat podle názvu týmu nebo soutěže" value={matchQuery} onChange={(e)=>setMatchQuery(e.target.value)} />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Napojení</FormLabel>
              {state?.externalMatchId ? <Badge ml={3} colorScheme="blue">Zápas: {state.externalMatchId}</Badge> : <Badge ml={3}>Bez zápasu</Badge>}
            </FormControl>
          </SimpleGrid>

          <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" size="sm">
            <TabList>
              <Tab>Nadcházející</Tab>
              <Tab>Nedávné</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={2} maxH="320px" overflowY="auto">
                  {parsedMatches.upcoming.map((m) => {
                    const homeLogo = getLogo(String(m.home), m.home_logo_url || (m as any).homeLogoURL || '');
                    return (
                      <HStack key={`${m.match_id || m.id}`} spacing={3} p={2} borderWidth="1px" borderRadius="md" _hover={{ bg: 'gray.50' }}>
                        {homeLogo ? <Image src={resolveLogoUrl(homeLogo)} alt={String(m.home)} boxSize="28px" objectFit="contain" /> : null}
                        <Text fontWeight="semibold">{String(m.home)} vs {String(m.away)}</Text>
                        <Badge ml="auto">{String(m.date_time || m.date || '').slice(0,16).replace('T',' ')}</Badge>
                        <Button size="sm" onClick={() => applyMatch(m)}>Vybrat zápas</Button>
                      </HStack>
                    );
                  })}
                </VStack>
              </TabPanel>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={2} maxH="320px" overflowY="auto">
                  {parsedMatches.recent.map((m) => {
                    const homeLogo = getLogo(String(m.home), m.home_logo_url || (m as any).homeLogoURL || '');
                    return (
                      <HStack key={`${m.match_id || m.id}`} spacing={3} p={2} borderWidth="1px" borderRadius="md" _hover={{ bg: 'gray.50' }}>
                        {homeLogo ? <Image src={resolveLogoUrl(homeLogo)} alt={String(m.home)} boxSize="28px" objectFit="contain" /> : null}
                        <Text fontWeight="semibold">{String(m.home)} vs {String(m.away)}</Text>
                        <Badge ml="auto" colorScheme="purple">{String(m.date_time || m.date || '').slice(0,16).replace('T',' ')}</Badge>
                        <Button size="sm" variant="outline" onClick={() => applyMatch(m)}>Vybrat zápas</Button>
                      </HStack>
                    );
                  })}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <HStack mt={3}>
            <Button
              variant="ghost"
              onClick={async () => {
                const next = await saveScoreboardState({ externalMatchId: '' });
                setState(next);
                toast({ title: 'Odpojeno', description: 'Tabule odpojena od zápasu.', status: 'info' });
              }}
            >Odpojit od zápasu</Button>
          </HStack>
        </Box>

        {/* Live preview */}
        <Box display="flex" justifyContent="center" mb={6}>
          <ScoreboardPreview state={previewState as ScoreboardState} />
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box>
            <Heading size="md" mb={3}>Základní údaje</Heading>
            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>Domácí tým</FormLabel>
                <Input
                  value={state.homeName}
                  onChange={async (e) => {
                    const v = e.target.value;
                    await setPartial({ homeName: v, homeShort: state.homeShort?.trim() ? state.homeShort : deriveShort(v) });
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Zkratka domácích</FormLabel>
                <Input
                  value={state.homeShort || ''}
                  maxLength={3}
                  onChange={async (e) => {
                    await setPartial({ homeShort: e.target.value.toUpperCase().slice(0,3) });
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Hostující tým</FormLabel>
                <Input
                  value={state.awayName}
                  onChange={async (e) => {
                    const v = e.target.value;
                    await setPartial({ awayName: v, awayShort: state.awayShort?.trim() ? state.awayShort : deriveShort(v) });
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Zkratka hostů</FormLabel>
                <Input
                  value={state.awayShort || ''}
                  maxLength={3}
                  onChange={async (e) => {
                    await setPartial({ awayShort: e.target.value.toUpperCase().slice(0,3) });
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Logo domácích (URL)</FormLabel>
                <Input
                  value={state.homeLogo || ''}
                  onChange={async (e) => {
                    await setPartial({ homeLogo: e.target.value });
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Logo hostů (URL)</FormLabel>
                <Input
                  value={state.awayLogo || ''}
                  onChange={async (e) => {
                    await setPartial({ awayLogo: e.target.value });
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Délka poločasu (min)</FormLabel>
                <NumberInput value={state.halfLength} min={1} max={60} onChange={async (_, n) => setPartial({ halfLength: Number.isFinite(n) ? n : 45 })}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>Styl</FormLabel>
                <Select value={state.theme} onChange={async (e) => setPartial({ theme: e.target.value as ScoreboardTheme })}>
                  {themes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Poločas</FormLabel>
                <Select value={String(state.half || 1)} onChange={async (e) => setPartial({ half: parseInt(e.target.value, 10) || 1 })}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </Select>
              </FormControl>
            </SimpleGrid>
          </Box>

          <Box>
            <Heading size="md" mb={3} color="black">Barvy a vyhledání klubu</Heading>
            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>Barva domácích</FormLabel>
                <ColorPicker
                  value={primaryDraft}
                  showAlpha
                  onChange={(hex) => {
                    // Update ref for instant preview, state for display
                    primaryDraftRef.current = hex;
                    setPrimaryDraft(hex);
                  }}
                  onChangeComplete={(hex) => {
                    primaryDraftRef.current = hex;
                    setPrimaryDraft(hex);
                    commitDraftColors();
                  }}
                  recentStorageKey="scoreboard-primary"
                  onInteractionStart={() => setIsPickingColor(true)}
                  onInteractionEnd={() => setIsPickingColor(false)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Barva hostů</FormLabel>
                <ColorPicker
                  value={secondaryDraft}
                  showAlpha
                  onChange={(hex) => {
                    // Update ref for instant preview, state for display
                    secondaryDraftRef.current = hex;
                    setSecondaryDraft(hex);
                  }}
                  onChangeComplete={(hex) => {
                    secondaryDraftRef.current = hex;
                    setSecondaryDraft(hex);
                    commitDraftColors();
                  }}
                  recentStorageKey="scoreboard-secondary"
                  onInteractionStart={() => setIsPickingColor(true)}
                  onInteractionEnd={() => setIsPickingColor(false)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Barva textu domácích</FormLabel>
                <ColorPicker
                  value={homeTextDraft}
                  showAlpha
                  onChange={(hex) => {
                    // Update ref for instant preview, state for display
                    homeTextDraftRef.current = hex;
                    setHomeTextDraft(hex);
                  }}
                  onChangeComplete={(hex) => {
                    homeTextDraftRef.current = hex;
                    setHomeTextDraft(hex);
                    commitDraftColors();
                  }}
                  recentStorageKey="scoreboard-home-text"
                  onInteractionStart={() => setIsPickingColor(true)}
                  onInteractionEnd={() => setIsPickingColor(false)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Barva textu hostů</FormLabel>
                <ColorPicker
                  value={awayTextDraft}
                  showAlpha
                  onChange={(hex) => {
                    // Update ref for instant preview, state for display
                    awayTextDraftRef.current = hex;
                    setAwayTextDraft(hex);
                  }}
                  onChangeComplete={(hex) => {
                    awayTextDraftRef.current = hex;
                    setAwayTextDraft(hex);
                    commitDraftColors();
                  }}
                  recentStorageKey="scoreboard-away-text"
                  onInteractionStart={() => setIsPickingColor(true)}
                  onInteractionEnd={() => setIsPickingColor(false)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>QR interval (minuty)</FormLabel>
                <NumberInput value={state.qrEvery || 5} min={1} max={120} onChange={async (_, n) => setPartial({ qrEvery: Math.max(1, Number.isFinite(n) ? n : 5) })}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>QR délka zobrazení (sekundy)</FormLabel>
                <NumberInput value={state.qrDuration || 60} min={5} max={600} onChange={async (_, n) => setPartial({ qrDuration: Math.max(5, Number.isFinite(n) ? n : 60) })}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </SimpleGrid>

            <Divider my={4} />
            <HStack spacing={3} align="flex-start">
              <Select value={assignTo} onChange={(e) => setAssignTo((e.target.value as 'home'|'away') || 'home')} maxW="160px">
                <option value="home">Nastavit domácí</option>
                <option value="away">Nastavit hosty</option>
              </Select>
              <Input placeholder="Hledat klub (FAČR)" value={clubQuery} onChange={(e) => setClubQuery(e.target.value)} />
              <Button isLoading={searchLoading} onClick={() => clubQuery.trim() && searchClubs(clubQuery.trim())}>Hledat</Button>
            </HStack>
            {searchError ? (
              <Text color="red.500" mt={2}>Chyba vyhledávání: {searchError.message}</Text>
            ) : null}
            <VStack align="stretch" spacing={2} mt={3} maxH="260px" overflowY="auto">
              {searchResults?.slice(0, 8).map((r) => (
                <HStack key={`${r.club_type}-${r.club_id}`} spacing={3} p={2} borderWidth="1px" borderRadius="md" _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => applyClub(r)}>
                  {r.logo_url ? <Image src={resolveLogoUrl(r.logo_url)} alt={r.name} boxSize="28px" objectFit="contain" /> : null}
                  <Box>
                    <Text fontWeight="medium" color="black">{r.name}</Text>
                    <Text fontSize="sm" color="black">{r.club_type}</Text>
                  </Box>
                </HStack>
              ))}
            </VStack>
          </Box>
        </SimpleGrid>

        <Divider my={6} />

        <HStack spacing={3}>
          <Button variant="outline" onClick={() => setPartial({ homeScore: 0, awayScore: 0 })}>Reset skóre</Button>
          <Button variant="outline" onClick={async () => { await resetTimer(); const s = await getScoreboardState(); setState(s); }}>Reset čas</Button>
          <Button variant="outline" onClick={() => setPartial({ homeFouls: 0, awayFouls: 0 })}>Reset fauly</Button>
        </HStack>

        <Divider my={6} />

        {/* Časovač ovládá pouze mobilní ovladač. Na této stránce ponechány pouze reset akce. */}

        <Box borderWidth="1px" borderRadius="lg" p={4} bg={cardBg} mb={6}>
          <Heading size="md" mb={3}>Sponzoři (overlay)</Heading>
          {(() => { const href = (typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '') + '/overlay/sponsors'; return (
            <HStack spacing={3} mb={3}>
              <Badge colorScheme="green">OBS</Badge>
              <Button as="a" href={href} target="_blank" rel="noreferrer">Otevřít overlay sponzoři</Button>
              <Text fontSize="sm" color="gray.500">Veřejná URL: {href}</Text>
            </HStack>
          ); })()}
          <HStack spacing={3} mb={3} align="flex-start">
            <Box flex="1">
              <UploadPanel
                label="Nahrát loga"
                description="Nahrajte loga sponzorů pro overlay v přenosu."
                accept="image/*,image/svg+xml"
                multiple
                maxFiles={50}
                uploadFn={uploadSponsorFile}
                allowUrlImport={false}
              />
            </Box>
            <VStack align="stretch" spacing={2} minW="180px">
              <Button variant="ghost" onClick={async ()=>{ try { setSponsors(await listSponsorsAdmin()); toast({ title: 'Seznam aktualizován', status: 'info' }); } catch {} }}>Obnovit</Button>
              <Button variant="outline" onClick={async ()=>{
                try {
                  const res = await prefillSponsorsFromPage();
                  setSponsors(await listSponsorsAdmin());
                  toast({ title: 'Předvyplněno ze Sponzorů', description: `Přidáno ${res?.saved || 0} log`, status: 'success' });
                } catch (e:any) {
                  toast({ title: 'Předvyplnění selhalo', description: e?.message, status: 'error' });
                }
              }}>Předvyplnit ze stránky Sponzoři</Button>
              <Button colorScheme="red" variant="outline" isDisabled={!sponsors.length} onClick={openBulkDelete}>Smazat vše</Button>
            </VStack>
          </HStack>
          <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={3}>
            {sponsors.map((u) => {
              const name = (u || '').split('/').pop() || '';
              return (
                <VStack key={u} spacing={2} borderWidth="1px" borderRadius="md" p={2}>
                  <Image src={u} alt={name} boxSize="64px" objectFit="contain" />
                  <Text fontSize="xs" noOfLines={1} maxW="120px">{name}</Text>
                  <Button size="xs" colorScheme="red" variant="ghost" onClick={async ()=>{ try { await deleteSponsor(name); setSponsors(await listSponsorsAdmin()); toast({ title: 'Smazáno', status: 'success' }); } catch { toast({ title: 'Smazání selhalo', status: 'error' }); } }}>Smazat</Button>
                </VStack>
              );
            })}
          </SimpleGrid>
        </Box>

        {/* Modal: Add uploaded logos as sponsors */}
        <Modal isOpen={isSponsorModalOpen} onClose={closeSponsorModal} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Přidat loga jako sponzory?</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={3}>Vyplňte název a (volitelně) web pro každé nahrané logo. Záznamy bez názvu budou přeskočeny.</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {uploadedNewSponsors.map((it, idx) => (
                  <VStack key={it.urlAbs} align="stretch" spacing={2} borderWidth="1px" borderRadius="md" p={3}>
                    <Image src={it.urlAbs} alt="logo" boxSize="64px" objectFit="contain" />
                    <FormControl>
                      <FormLabel>Název</FormLabel>
                      <Input value={it.name} onChange={(e)=>{
                        const v = e.target.value;
                        setUploadedNewSponsors((prev) => prev.map((x,i) => i===idx ? { ...x, name: v } : x));
                      }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Web (https://…)</FormLabel>
                      <Input value={it.website} onChange={(e)=>{
                        const v = e.target.value;
                        setUploadedNewSponsors((prev) => prev.map((x,i) => i===idx ? { ...x, website: v } : x));
                      }} />
                    </FormControl>
                  </VStack>
                ))}
              </SimpleGrid>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={closeSponsorModal}>Ne</Button>
              <Button colorScheme="blue" isLoading={addingSponsors} onClick={async ()=>{
                try {
                  setAddingSponsors(true);
                  for (const it of uploadedNewSponsors) {
                    const name = (it.name || '').trim();
                    if (!name) continue;
                    try {
                      await createSponsor({ name, logo_url: it.url, website_url: (it.website || '').trim() || undefined, is_active: true });
                    } catch {}
                  }
                  toast({ title: 'Sponzoři přidáni', status: 'success' });
                } finally {
                  setAddingSponsors(false);
                  closeSponsorModal();
                  setUploadedSponsorUrls([]);
                  setUploadedNewSponsors([]);
                }
              }}>Ano, přidat</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Bulk delete confirm */}
        <AlertDialog isOpen={isBulkDeleteOpen} leastDestructiveRef={cancelBulkRef} onClose={closeBulkDelete}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Smazat všechna loga?</AlertDialogHeader>
              <AlertDialogBody>
                Tato akce odstraní všechna nahraná loga pro overlay sponzorů. Operaci nelze vrátit zpět.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelBulkRef} onClick={closeBulkDelete} mr={3}>Zrušit</Button>
                <Button colorScheme="red" isLoading={isBulkDeleting} onClick={async ()=>{
                  try {
                    setIsBulkDeleting(true);
                    const current = [...sponsors];
                    for (const u of current) {
                      const name = (u || '').split('/').pop() || '';
                      if (!name) continue;
                      try { await deleteSponsor(name); } catch {}
                    }
                    setSponsors(await listSponsorsAdmin());
                    toast({ title: 'Všechna loga smazána', status: 'success' });
                  } finally {
                    setIsBulkDeleting(false);
                    closeBulkDelete();
                  }
                }}>Smazat vše</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg={cardBg} mb={6}>
          <Heading size="md" mb={3}>QR kód</Heading>
          <HStack spacing={4} align="flex-start" flexWrap="wrap">
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.500">Aktuální QR:</Text>
              {qrUrl ? (
                <Image src={qrUrl} alt="QR" boxSize="128px" objectFit="contain" borderWidth="1px" borderRadius="md" />
              ) : (
                <Text fontSize="sm" color="gray.400">Nenahrán</Text>
              )}
            </VStack>
            <Box maxW="360px" flex="1">
              <UploadPanel
                label="Nahrát QR"
                description="Nahrajte obrázek QR kódu pro scoreboard."
                accept="image/*"
                multiple={false}
                maxFiles={1}
                uploadFn={uploadQrFile}
                allowUrlImport={false}
              />
            </Box>
            <Button variant="ghost" onClick={async ()=>{ try { setQrUrl(await getQr()); toast({ title: 'Obnoveno', status: 'info' }); } catch {} }}>Obnovit</Button>
            <Button variant="outline" colorScheme="red" isDisabled={!qrUrl} onClick={async ()=>{
              try { await deleteQr(); setQrUrl(''); toast({ title: 'QR smazán', status: 'info' }); } catch { toast({ title: 'Smazání selhalo', status: 'error' }); }
            }}>Smazat QR</Button>
          </HStack>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg={cardBg} mb={6}>
          <Heading size="md" mb={3}>Presety</Heading>
          <HStack spacing={3} align="center" flexWrap="wrap" mb={3}>
            <Input placeholder="Název presetu (např. derby-2025)"
                   value={presetName}
                   onChange={(e)=>setPresetName(e.target.value)}
                   maxW="260px" />
            <Button onClick={async ()=>{ try { await savePreset(presetName); setPresets(await listPresets()); setPresetName(''); toast({ title: 'Preset uložen', status: 'success' }); } catch (e:any){ toast({ title: 'Uložení selhalo', description: e?.message, status: 'error' }); } }}>Uložit preset</Button>
          </HStack>
          <HStack spacing={3} align="center" flexWrap="wrap">
            <Select placeholder="Vyberte preset" maxW="260px" onChange={(e)=>setPresetName(e.target.value)} value={presetName}>
              {presets.map((p)=> (<option key={p} value={p}>{p}</option>))}
            </Select>
            <Button variant="outline" onClick={async ()=>{ if (!presetName) { toast({ title: 'Vyberte preset', status: 'warning' }); return; } try { await loadPreset(presetName); setState(await getScoreboardState()); toast({ title: 'Preset načten', status: 'success' }); } catch (e:any){ toast({ title: 'Načtení selhalo', description: e?.message, status: 'error' }); } }}>Načíst preset</Button>
            <Button variant="ghost" onClick={async ()=>{ try { setPresets(await listPresets()); toast({ title: 'Seznam aktualizován', status: 'info' }); } catch {} }}>Obnovit</Button>
          </HStack>
        </Box>

        <Heading size="md" mb={3}>Import / Export</Heading>
        <HStack spacing={4} align="center" flexWrap="wrap">
          <Button
            onClick={() => {
              try {
                const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'scoreboard-state.json';
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast({ title: 'Export selhal', status: 'error' });
              }
            }}
          >Exportovat JSON</Button>
          <Button as="label">
            Importovat JSON
            <Input type="file" accept="application/json" display="none" onChange={async (e) => {
              try {
                const f = e.target.files?.[0];
                if (!f) return;
                const text = await f.text();
                const data = JSON.parse(text);
                // Patch via admin API when possible
                await updateAdminScoreboard(data);
                setState(await getScoreboardState());
                toast({ title: 'Import dokončen', status: 'success' });
              } catch (err: any) {
                toast({ title: 'Import selhal', description: err?.message || 'Neplatný soubor', status: 'error' });
              } finally {
                try { if (e.target) (e.target as HTMLInputElement).value = ''; } catch {}
              }
            }} />
          </Button>
        </HStack>

        <Divider my={8} />
        <Heading size="md" mb={2}>Návod k použití (CZ)</Heading>
        <VStack align="start" spacing={2} color="gray.700">
          <Text>1) V horní části vyberte soutěž, případně vyhledejte zápas podle názvu týmu. Klikněte na „Vybrat zápas“.</Text>
          <Text>2) Pole „Domácí/Hosté“, loga a zkratky se předvyplní. Barvy se pokusíme odhadnout z log.</Text>
          <Text>3) Skóre upravujte tlačítky níže nebo ručním zadáním. Změny se ukládají průběžně.</Text>
          <Text>4) Jakmile je tabule propojená se zápasem, živé skóre se propisuje do veřejné homepage (nejbližší zápas) a do JSON cache.</Text>
          <Text>5) Přepněte vzhled tabule (styl) dle potřeby. Pro přenos do streamu použijte veřejnou adresu /overlay/scoreboard.</Text>
          <Text>Tip: Při importu JSON použijte předchozí export, formát je kompatibilní.</Text>
        </VStack>
      </Box>
    </AdminLayout>
  );
};

export default ScoreboardAdminPage;
